// ─────────────────────────────────────────────────────────────────────────────
// Sidebar.jsx  –  Main navigation drawer + top AppBar
//
// Props:
//   open     {boolean}  – whether the drawer is expanded
//   setOpen  {Function} – toggle function for the drawer
//
// Key behaviours:
//   • "isDirty" guard  – if a form has unsaved changes, navigating away opens a
//     confirmation dialog instead of navigating immediately.
//   • Pause-Appointments toggle  – top-bar switch that pauses all incoming
//     appointment requests for 24 h (fetched & saved via Redux actions).
//   • Platform gating  – nav items are only rendered when the user's
//     allowedPlatforms includes 'web'; a "Switch to Premium" CTA is shown for
//     mobile-only accounts.
//   • Active-state highlighting  – each item/accordion reads location.pathname
//     to apply the 'sidebarActive' / 'sidebarActiveInside' CSS classes.
//   • Unread badge  – Messenger shows unread count from Zustand (synced in App);
//     Dashboard shows a totalReqCount badge.
// ─────────────────────────────────────────────────────────────────────────────

import AddIcon from '@mui/icons-material/Add';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CallIcon from '@mui/icons-material/Call';
import ChecklistIcon from '@mui/icons-material/Checklist';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpIcon from '@mui/icons-material/Help';
import HomeIcon from '@mui/icons-material/Home';
import PeopleIcon from '@mui/icons-material/People';
import ReceiptIcon from '@mui/icons-material/Receipt';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import SettingsIcon from '@mui/icons-material/Settings';
import SmsIcon from '@mui/icons-material/Sms';
import { Accordion, AccordionDetails, AccordionSummary, Button, Menu, MenuItem, Stack, Switch, Tooltip } from '@mui/material';
import MuiAppBar from '@mui/material/AppBar';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import MuiDrawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { styled, useTheme } from '@mui/material/styles';
import * as React from 'react';
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { Grade } from '@mui/icons-material';
import axios from 'axios';
import { Context } from '../../App';
import ApexIcon from '../../assets/Logo_SaaS.svg';
import SideBarIcon from '../../assets/sidebar-button-icon.svg';
import CommonDialog from '../../common/CommonDialog';
import queryClient from '../../config/queryClient';
import { CustomersListBasedOnType } from '../../redux/Invoice/action';
import { editPauseAppointmentStatus, getPauseAppointmentStatus } from '../../redux/PauseAppointment/action';
import { useQuickReviewUsers } from '../../services/business/reviewRequest/reviewRequest.hooks.js';
import { getCountryMasterListApi } from '../../services/core/master/master.api.js';
import { coreMasterKeys } from '../../services/core/master/master.hooks.js';
import useEstimateStore from '../../store/useEstimateStore';
import useUnreadStore from '../../store/useUnreadStore';
import useGlobalStore from '../../store/useGlobalStore';
import useInvoiceStore from '../../store/useInvoiceStore';
import ReveiwRequestSvg from '../../utility/Svg/ReveiwRequestSvg';
import { getItem, setItem } from '../../utility/localStorage';
import SuccessMessageModal from '../Common/SuccessMessage';
import NotificationPopUp from './NotificationPopUp';
import ShopPopup from './ShopPopup';
import './Sidebar.css';
import { toast } from 'sonner';
import AddQuickReviewModal from '../../common/AddQuickReviewModal';

// ─── Constants ───────────────────────────────────────────────────────────────

const DRAWER_WIDTH = 288;

// ─── MUI styled helpers ───────────────────────────────────────────────────────

/** Styles applied to the Drawer paper when it is fully open */
const openedMixin = (theme) => ({
    width: DRAWER_WIDTH,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
    overflowY: 'overlay',
});

/** Styles applied to the Drawer paper when it is collapsed to icon-only width */
const closedMixin = (theme) => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    overflowY: 'overlay',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
});

/** Spacer div that pushes content below the fixed AppBar */
const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
}));

/** AppBar that shifts its width/margin when the drawer opens/closes */
const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open
        ? {
              marginLeft: DRAWER_WIDTH,
              width: `calc(100% - ${DRAWER_WIDTH}px)`,
              transition: theme.transitions.create(['width', 'margin'], {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.enteringScreen,
              }),
          }
        : {
              marginLeft: '50px',
              width: `calc(100% - 62.5px)`,
              [theme.breakpoints.down('md')]: { width: `calc(100% - 62px)` },
              [theme.breakpoints.down('sm')]: { width: `calc(100% - 57px)` },
              transition: theme.transitions.create(['width', 'margin'], {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.enteringScreen,
              }),
          }),
}));

/** Drawer that animates between open and closed mixins */
const Drawer = styled(MuiDrawer, {
    shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
    width: DRAWER_WIDTH,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open ? { ...openedMixin(theme), '& .MuiDrawer-paper': openedMixin(theme) } : { ...closedMixin(theme), '& .MuiDrawer-paper': closedMixin(theme) }),
}));

// ─── SVG icon components (inline SVGs that are not in @mui/icons-material) ───

/** Inventory box icon */
const InventoryIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path
            d="M19.993 6.86872C19.9911 6.79655 19.9814 6.7248 19.964 6.65472C19.957 6.62972 19.943 6.60572 19.934 6.58072C19.913 6.52372 19.894 6.46772 19.864 6.41572C19.848 6.38872 19.826 6.36672 19.807 6.34072C19.775 6.29572 19.744 6.24972 19.705 6.21072C19.682 6.18872 19.652 6.17072 19.627 6.14972C19.588 6.11772 19.552 6.08272 19.507 6.05572L19.493 6.04972L19.485 6.04372L10.506 1.05372C10.3577 0.971266 10.1908 0.92791 10.0211 0.927735C9.85143 0.92756 9.68449 0.970572 9.536 1.05272L0.515 6.04272C0.512333 6.04605 0.508667 6.04939 0.504 6.05272L0.494 6.05672C0.459 6.07672 0.433 6.10572 0.4 6.12972C0.364 6.15672 0.326 6.18072 0.294 6.21172C0.264 6.24272 0.241 6.27872 0.215 6.31372C0.189 6.34872 0.158 6.37972 0.136 6.41772C0.11 6.46072 0.0959999 6.50972 0.0769999 6.55672C0.0629999 6.58972 0.045 6.62072 0.036 6.65672C0.0189199 6.7255 0.00919862 6.79589 0.00699997 6.86672C0.00599997 6.88372 0 6.89872 0 6.91672V14.9187C0 15.2817 0.197 15.6167 0.515 15.7927L9.493 20.7797L9.494 20.7807L9.496 20.7817L9.516 20.7927C9.559 20.8167 9.606 20.8297 9.651 20.8467C9.683 20.8597 9.714 20.8767 9.748 20.8857C9.91388 20.9291 10.0881 20.9291 10.254 20.8857C10.287 20.8767 10.318 20.8597 10.351 20.8467C10.396 20.8297 10.443 20.8177 10.486 20.7927L10.506 20.7817L10.508 20.7807L10.509 20.7797L19.487 15.7927C19.803 15.6167 20 15.2817 20 14.9187V6.91672C20 6.89972 19.994 6.88572 19.993 6.86872ZM9.972 10.7907L3.058 6.92372L5.82 5.39572L12.654 9.30072L9.972 10.7907ZM10.02 3.07172L16.941 6.91872L14.697 8.16572L7.867 4.26272L10.02 3.07172ZM11 18.2197L11.002 12.5407L14 10.8627V13.9187L16 12.9187V9.74372L18 8.62472V14.3297L11 18.2197Z"
            fill="white"
        />
    </svg>
);

/** Clipboard-with-check icon used for Work Orders */
const WorkOrderIcon = ({ color = 'white', style }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" style={style}>
        <path d="M9.16667 18.3321H2.50003L2.5 2.91699H5.83337M12.5 2.91699H15.8333V11.667" stroke={color} strokeWidth="1.5" strokeLinecap="square" />
        <path
            d="M11.2497 1.66699H7.08301C6.39265 1.66699 5.83301 2.22663 5.83301 2.91699C5.83301 3.60735 6.39265 4.16699 7.08301 4.16699H11.2497C11.94 4.16699 12.4997 3.60735 12.4997 2.91699C12.4997 2.22663 11.94 1.66699 11.2497 1.66699Z"
            fill={color}
            stroke={color}
            strokeWidth="1.5"
            strokeLinecap="square"
        />
        <path d="M5.83301 12.5003H8.69017M5.83301 9.16699H12.4997" stroke={color} strokeWidth="1.5" strokeLinecap="square" />
        <path d="M17.5003 14.167L13.3337 18.3337L11.667 16.667" stroke={color} strokeWidth="1.5" strokeLinecap="square" />
    </svg>
);

/** Delivery-truck icon used for Suppliers */
const SupplierIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
        <path
            d="M0.75 8.41895H10.5L11.25 9.91895H1.5L0.75 8.41895ZM1.75 11.4189H11.5L12.25 12.9189H2.5L1.75 11.4189ZM18 19.4189C18.83 19.4189 19.5 18.7489 19.5 17.9189C19.5 17.0889 18.83 16.4189 18 16.4189C17.17 16.4189 16.5 17.0889 16.5 17.9189C16.5 18.7489 17.17 19.4189 18 19.4189ZM19.5 10.4189H17V12.9189H21.46L19.5 10.4189ZM8 19.4189C8.83 19.4189 9.5 18.7489 9.5 17.9189C9.5 17.0889 8.83 16.4189 8 16.4189C7.17 16.4189 6.5 17.0889 6.5 17.9189C6.5 18.7489 7.17 19.4189 8 19.4189ZM20 8.91895L23 12.9189V17.9189H21C21 19.5789 19.66 20.9189 18 20.9189C16.34 20.9189 15 19.5789 15 17.9189H11C11 19.5789 9.65 20.9189 8 20.9189C6.34 20.9189 5 19.5789 5 17.9189H3V14.4189H5V15.9189H5.76C6.31 15.3089 7.11 14.9189 8 14.9189C8.89 14.9189 9.69 15.3089 10.24 15.9189H15V6.91895H3C3 5.80895 3.89 4.91895 5 4.91895H17V8.91895H20Z"
            fill="white"
        />
    </svg>
);

// ─── Reusable sub-components ──────────────────────────────────────────────────

/**
 * NavItem  –  A simple, non-expandable sidebar row.
 *
 * Props:
 *   icon         {ReactNode}  – icon element
 *   label        {string}     – display text (also used as tooltip when collapsed)
 *   path         {string}     – route to navigate to on click
 *   activeSegment {string}   – the URL segment that marks this item as active
 *                              (compared against location.pathname.split('/')[1])
 *   open         {boolean}    – whether the drawer is expanded
 *   onClick      {Function}   – navigation handler (handles dirty-state guard)
 *   badge        {number}     – optional unread / count badge value
 *   location     {object}     – React Router location object
 */
const NavItem = ({ icon, label, path, activeSegment, open, onClick, badge, location }) => {
    const isActive = location.pathname.split('/')[1] === activeSegment || (activeSegment === '' && location.pathname === '/');

    return (
        <Tooltip title={!open && label} placement="right">
            <ListItem
                key={label}
                disablePadding
                sx={{
                    display: 'block',
                    marginLeft: !open && '4px',
                    marginBottom: !open && '6px',
                }}
                onClick={() => onClick(path)}
                className={(isActive ? 'sidebarActive' : '') + (!open ? ' sidebarListItem' : ' ')}
            >
                <ListItemButton
                    sx={{
                        minHeight: 48,
                        justifyContent: open ? 'initial' : 'center',
                        px: 2.5,
                        marginLeft: isActive && '-3px',
                    }}
                >
                    <ListItemIcon
                        sx={{
                            minWidth: 0,
                            mr: open ? 3 : 'auto',
                            justifyContent: 'center',
                            color: 'white',
                            position: 'relative',
                        }}
                    >
                        {icon}
                        {/* Numeric badge (e.g. unread messages, pending requests) */}
                        {badge > 0 && <span className="badge">{badge > 99 ? '99+' : badge}</span>}
                    </ListItemIcon>
                    <ListItemText primary={label} sx={{ opacity: open ? 1 : 0 }} />
                </ListItemButton>
            </ListItem>
        </Tooltip>
    );
};

/**
 * NavSubItem  –  A child row rendered inside an accordion's AccordionDetails.
 *
 * Props:
 *   label        {string}   – display text
 *   path         {string}   – route to navigate to on click
 *   segment      {string}   – URL segment checked for active styling
 *                             (compared against location.pathname.split('/')[2])
 *   open         {boolean}  – drawer expanded state (controls opacity)
 *   onClick      {Function} – navigation handler
 *   location     {object}   – React Router location object
 *   extraStyle   {object}   – optional additional inline styles on the ListItem
 */
const NavSubItem = ({ label, path, segment, open, onClick, location, extraStyle = {} }) => {
    // Match the module as well as the segment — every section has an "all", so comparing the segment
    // alone lit up "All" under Invoices, Estimates and Work Orders at the same time.
    const [, currentModule, currentSegment] = location.pathname.split('/');
    const [, itemModule] = path.split('/');
    const isActive = currentModule === itemModule && currentSegment === segment;

    return (
        <ListItem key={label} disablePadding onClick={() => onClick(path)} style={extraStyle}>
            {/* Left spacer – aligns sub-items to the right of the parent icon */}
            <ListItemIcon sx={{ minWidth: 0, mr: open ? 5 : 'auto', mt: 0, color: 'white' }} />
            <ListItemButton className={(isActive ? 'sidebarActiveInside' : '') + (!open ? ' sidebarListItem' : ' ')}>
                <ListItemIcon sx={{ minWidth: 0, mr: open ? 5 : 'auto', mt: 0, color: 'white' }}>
                    <ListItemText sx={{ opacity: open ? 1 : 0 }}>
                        <span style={{ fontSize: '14px' }}>{label}</span>
                    </ListItemText>
                </ListItemIcon>
            </ListItemButton>
        </ListItem>
    );
};

/**
 * NavAccordion  –  An expandable nav section used when the drawer is open.
 * When the drawer is collapsed, it renders a plain icon-only NavItem instead.
 *
 * Props:
 *   icon            {ReactNode}   – section icon
 *   label           {string}      – section title
 *   rootPath        {string}      – route navigated to when clicking the header text
 *   activeSegment   {string}      – URL segment that marks this section as active
 *   open            {boolean}     – drawer expanded state
 *   onClick         {Function}    – navigation handler
 *   location        {object}      – React Router location object
 *   children        {ReactNode}   – NavSubItem children rendered in AccordionDetails
 *   accordionSummaryClassName {string} – optional extra class on AccordionSummary
 */
const NavAccordion = ({ icon, label, rootPath, activeSegment, open, onClick, location, children, accordionSummaryClassName = '' }) => {
    const isActive = location.pathname.split('/')[1] === activeSegment;

    // Collapsed drawer – just show the icon, no accordion
    if (!open) {
        return (
            <Tooltip title={label} placement="right">
                <ListItem
                    key={label}
                    disablePadding
                    sx={{ display: 'block', marginLeft: '4px', marginBottom: '6px' }}
                    className={(isActive ? 'sidebarActive' : '') + ' sidebarListItem'}
                    onClick={() => onClick(rootPath)}
                >
                    <ListItemButton
                        sx={{
                            minHeight: 48,
                            justifyContent: 'center',
                            px: 2.5,
                            marginLeft: isActive && '-3px',
                        }}
                    >
                        <ListItemIcon sx={{ minWidth: 0, mr: 'auto', justifyContent: 'center', color: 'white' }}>{icon}</ListItemIcon>
                    </ListItemButton>
                </ListItem>
            </Tooltip>
        );
    }

    // Expanded drawer – show full accordion with sub-items
    return (
        <Accordion style={{ color: 'white', background: '#002394', '&:before': { display: 'none' } }} disableGutters variant="elevation" elevation={0}>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon style={{ color: 'white' }} />}
                style={{ paddingLeft: 'inherit', marginBottom: '0px' }}
                className={accordionSummaryClassName}
            >
                {/* Clicking the text navigates; the expand chevron just toggles the accordion */}
                <ListItem
                    key={label}
                    disablePadding
                    sx={{ display: 'block' }}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation(); // prevent accordion from toggling on text click
                        onClick(rootPath);
                    }}
                >
                    <ListItemButton sx={{ minHeight: 48, justifyContent: 'initial', px: 2.5 }}>
                        <ListItemIcon sx={{ minWidth: 0, mr: 3, justifyContent: 'center', color: 'white' }}>{icon}</ListItemIcon>
                        <ListItemText primary={label} sx={{ opacity: 1 }} />
                    </ListItemButton>
                </ListItem>
            </AccordionSummary>
            <AccordionDetails style={{ padding: '8px 0px 15px 12px' }}>{children}</AccordionDetails>
        </Accordion>
    );
};

/**
 * NavSectionLabel  –  A small uppercase section heading shown between nav groups.
 *
 * Props:
 *   label  {string}   – section name (e.g. "FRONT DESK")
 *   open   {boolean}  – drawer expanded state
 *                       When collapsed, a thin Divider is rendered instead of
 *                       the text label so the icon-only layout stays clean.
 */
const NavSectionLabel = ({ label, open, showDivider = true }) => {
    if (!open) {
        // Collapsed drawer – just a subtle divider between sections
        return showDivider ? <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.60)', width: '18px', ml: 2.5, mb: 0.5 }} /> : <></>;
        // return <></>;
    }

    return (
        <div
            style={{
                padding: '0',
                marginBottom: '12px',
                marginTop: '16px',
                color: 'rgba(255, 255, 255, 0.60)',
                fontSize: '10px',
                fontWeight: 700,
                lineHeight: '120%',
                letterSpacing: '-0.2px',
                textTransform: 'uppercase',
                textAlign: 'start',
            }}
        >
            {label}
        </div>
    );
};

// ─── Main component ───────────────────────────────────────────────────────────

export default function Dashboard({ open, setOpen }) {
    const navigate = useNavigate();
    const theme = useTheme();
    const location = useLocation();
    const dispatch = useDispatch();

    // Unread messenger count (listener lives in App via CometChatUnreadSync)
    const unreadCount = useUnreadStore((s) => s.unreadCount);

    const [isAddModalOpen, setIsAddModalOpen] = React.useState(false);
    const cancelTokenSourceRef = useRef(null);

    const [prefetchedCustomersPage1, setPrefetchedCustomersPage1] = React.useState([]);
    const [prefetchedCustomersPage1Meta, setPrefetchedCustomersPage1Meta] = React.useState(null);

    // Keep quick-review users warm in cache so modal opens instantly
    const { data: quickReviewUsers } = useQuickReviewUsers({ limit: 5 }, { enabled: true });

    // Global dirty-state guard (prevents accidental navigation away from unsaved forms)
    const { isDirty, resetGlobalState, totalReqCount, isSettingsDirty, resetSettingsDirty } = useGlobalStore();
    const { resetInvoiceStore } = useInvoiceStore();
    const { resetEstimateStore } = useEstimateStore();

    // Sidebar collapse state is persisted to localStorage via Context
    const { setIsOpenSidebar } = React.useContext(Context);

    // Auth data – re-read on custom storage event so plan changes reflect immediately
    const [authData, setAuthData] = React.useState(JSON.parse(localStorage.getItem('apex-saas_details')));

    // ── Unsaved-changes dialog state ─────────────────────────────────────────
    const [discardModalOpen, setDiscardModalOpen] = React.useState(false);
    const [pendingNavigation, setPendingNavigation] = React.useState(null);

    // ── Pause-appointments toggle state ─────────────────────────────────────
    const [openSuccessModal, setOpenSuccessModal] = React.useState(false);
    const [successMessage, setSuccessMessage] = React.useState('');
    const [statusValue, setStatusValue] = React.useState(false);
    const [disabledSwitch, setDisabledSwitch] = React.useState(false);

    // ── "NEW" button dropdown anchor ─────────────────────────────────────────
    const [anchorEl, setAnchorEl] = React.useState(null);
    const newMenuOpen = Boolean(anchorEl);

    // ─── Derived strings for the discard dialog ───────────────────────────────

    const pathSegments = location.pathname.split('/');

    // Determine what kind of document the user might be discarding
    const discardModalTitle = pathSegments.includes('record-payment')
        ? 'Record Payment'
        : pathSegments.includes('invoice')
          ? 'invoice'
          : pathSegments.includes('estimates')
            ? 'estimate'
            : pathSegments.includes('work-orders')
              ? 'work order'
              : 'invoice';

    const discardModalDescription = pathSegments.includes('record-payment')
        ? 'You have unsaved changes. Do you want to discard the recorded payments or continue editing?'
        : `You have unsaved changes. Do you want to discard this ${discardModalTitle} or continue editing?`;

    // ─── Navigation helpers ───────────────────────────────────────────────────

    /**
     * handleNavigation – navigate to `path`, but first check if there are
     * unsaved changes (isDirty). If so, stash the path and open the discard
     * dialog; otherwise navigate immediately.
     */
    const handleNavigation = (path) => {
        if (isDirty || isSettingsDirty) {
            setPendingNavigation(path);
            setDiscardModalOpen(true);
        } else {
            navigate(path);
            localStorage.removeItem('searchValue');
            resetInvoiceStore();
            resetEstimateStore();
        }
    };

    /** User confirmed "Discard" – clear dirty state and navigate */
    const handleDiscardConfirm = () => {
        resetGlobalState();
        resetSettingsDirty();
        setDiscardModalOpen(false);
        if (pendingNavigation) {
            navigate(pendingNavigation);
            localStorage.removeItem('searchValue');
            resetInvoiceStore();
            resetEstimateStore();
            setPendingNavigation(null);
        }
    };

    /** User cancelled the discard dialog – stay on current page */
    const handleDiscardCancel = () => {
        setDiscardModalOpen(false);
        setPendingNavigation(null);
    };

    // ─── Drawer open/close ────────────────────────────────────────────────────

    const handleDrawerOpen = () => {
        setOpen(true);
        setIsOpenSidebar(true);
        setItem('isCollapse', true);
    };

    const handleDrawerClose = () => {
        setOpen(false);
        setIsOpenSidebar(false);
        setItem('isCollapse', false);
    };

    // ─── "NEW" button dropdown ────────────────────────────────────────────────

    const handleOpenNewMenu = (e) => {
        if (e.currentTarget !== anchorEl) setAnchorEl(e.currentTarget);
    };
    const handleCloseNewMenu = () => {
        if (anchorEl !== null) setAnchorEl(null);
    };

    // ─── Pause-appointments ───────────────────────────────────────────────────

    /** Fetch the current pause status from the server on mount */
    const fetchAppointmentStatus = async () => {
        const resp = await dispatch(getPauseAppointmentStatus());
        if (resp) {
            setStatusValue(resp?.overallPauseAppointment !== '');
        }
    };

    /** Toggle pause status and show success toast */
    const updateAppointmentStatus = async (value) => {
        setDisabledSwitch(true);
        const resp = await dispatch(editPauseAppointmentStatus({ overallPauseAppointment: value }));
        if (resp) {
            setStatusValue(resp?.data?.overallPauseAppointment !== '');
            setOpenSuccessModal(true);
            setSuccessMessage(resp?.message);
        }
        setDisabledSwitch(false);
    };

    // ─── Effects ──────────────────────────────────────────────────────────────

    useEffect(() => {
        // Restore sidebar open/close state from localStorage on first render
        setIsOpenSidebar(getItem('isCollapse'));
    }, []);

    // Prefetch country + customer list on mount so AddQuickReviewModal is instant
    useEffect(() => {
        const shopCountryId = localStorage.getItem('shopCountryId') || '64e738882fec8f2c9b79c139';

        queryClient.prefetchQuery({
            queryKey: coreMasterKeys.countries({ countryId: shopCountryId }),
            queryFn: () => getCountryMasterListApi({ countryId: shopCountryId }),
        });

        const source = axios.CancelToken.source();
        cancelTokenSourceRef.current = source;

        dispatch(CustomersListBasedOnType('', ['shop_created', 'apex_auto_app', 'shop_imported', 'shop_guest'], 1, 50, source.token)).then((resp) => {
            if (resp?.data) {
                setPrefetchedCustomersPage1(resp.data);
                setPrefetchedCustomersPage1Meta({
                    totalPages: resp?.totalPages ?? 0,
                    currentPage: resp?.currentPage ?? 1,
                });
            }
        });

        return () => {
            if (cancelTokenSourceRef.current) cancelTokenSourceRef.current.cancel();
        };
    }, [dispatch]);

    useEffect(() => {
        fetchAppointmentStatus();
    }, []);

    useEffect(() => {
        // Re-read auth data when the billing plan changes (fired by a custom event)
        const handleStorageChange = () => {
            setAuthData(JSON.parse(localStorage.getItem('apex-saas_details')));
        };
        window.addEventListener('localStorageChangePlan', handleStorageChange);
        return () => window.removeEventListener('localStorageChangePlan', handleStorageChange);
    }, []);

    // ─── Pause-appointments styled switch ────────────────────────────────────
    //
    // This is defined inside the component so it can close over `disabledSwitch`
    // and `statusValue`. The switch turns red when paused (checked).

    const PauseAppointmentToggle = styled((props) => (
        <Switch
            disabled={disabledSwitch}
            focusVisibleClassName=".Mui-focusVisible"
            disableRipple
            {...props}
            defaultChecked={statusValue}
            onChange={(e) => updateAppointmentStatus(e.target.checked)}
        />
    ))(({ theme }) => ({
        width: 42,
        height: 26,
        padding: 0,
        '& .MuiSwitch-switchBase': {
            padding: 0,
            margin: 2,
            transitionDuration: '300ms',
            '&.Mui-checked': {
                transform: 'translateX(16px)',
                color: '#fff',
                '& + .MuiSwitch-track': {
                    backgroundColor: theme.palette.mode === 'dark' ? '#2ECA45' : '#d32f2f',
                    opacity: 1,
                    border: 0,
                },
                '&.Mui-disabled + .MuiSwitch-track': { opacity: 0.5 },
            },
            '&.Mui-focusVisible .MuiSwitch-thumb': { color: '#33cf4d', border: '6px solid #fff' },
            '&.Mui-disabled .MuiSwitch-thumb': {
                color: theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[600],
            },
            '&.Mui-disabled + .MuiSwitch-track': {
                opacity: theme.palette.mode === 'light' ? 0.7 : 0.3,
            },
        },
        '& .MuiSwitch-thumb': { boxSizing: 'border-box', width: 22, height: 22 },
        '& .MuiSwitch-track': {
            borderRadius: 26 / 2,
            backgroundColor: theme.palette.mode === 'light' ? 'rgba(120, 120, 128, 0.16)' : '#39393D',
            opacity: 1,
            transition: theme.transitions.create(['background-color'], { duration: 500 }),
        },
    }));

    // ─── Shared sx shorthand for sub-item ListItemIcon spacers ───────────────
    const subIconSx = (open) => ({ minWidth: 0, mr: open ? 5 : 'auto', mt: 0, color: 'white' });

    // ─── Render ───────────────────────────────────────────────────────────────

    return (
        <>
            <CssBaseline />

            {/* ── Top AppBar ─────────────────────────────────────────────────── */}
            <AppBar position="fixed" open={open} style={{ background: 'white', zIndex: 9 }}>
                <Toolbar>
                    {/* Left spacer (only visible when drawer is collapsed) */}
                    <div style={{ width: open ? '0%' : '50%', display: 'flex', justifyItems: 'left' }}>
                        <Typography variant="h6" noWrap component="div" />
                    </div>

                    {/* Right-side controls: Pause toggle, Notifications, Shop */}
                    <Stack spacing={2} direction="row" style={{ width: '100%', display: 'flex', justifyContent: 'end', alignItems: 'center' }}>
                        {/* Emergency Pause Appointments toggle */}
                        <div className="pauseAppointmentDiv">
                            <PauseAppointmentToggle size="large" />
                            <Tooltip
                                title={
                                    <span className="pauseAppointmentToolTip">
                                        Emergency Pause:
                                        <br />
                                        Activating this toggle will temporarily halt all new and incoming appointments requests. Customers will not be able to
                                        request appointment for any days.
                                        <br />
                                        This toggle will automatically turn off after 24 hours.
                                    </span>
                                }
                            >
                                <HelpIcon className="tooltipIcon" />
                            </Tooltip>
                        </div>

                        <div>
                            <NotificationPopUp />
                        </div>
                        <div>
                            <ShopPopup />
                        </div>
                    </Stack>
                </Toolbar>
            </AppBar>

            {/* ── Sidebar Drawer ─────────────────────────────────────────────── */}
            <Drawer variant="permanent" open={open} style={{ background: 'black' }}>
                {/* Logo + collapse button (only shown when expanded) */}
                <Stack spacing={2} direction="row" paddingInline={2} justifyContent="space-between" style={{ marginTop: '40px' }}>
                    {open && (
                        <>
                            <div style={{ display: 'block', width: '168px', height: '46.92px', cursor: 'pointer' }} onClick={handleDrawerClose}>
                                <img src={ApexIcon} style={{ objectFit: 'cover', width: 'inherit' }} alt="Logo" />
                            </div>
                            <IconButton color="default" size="small" onClick={handleDrawerClose}>
                                <img src={SideBarIcon} alt="Collapse sidebar" />
                            </IconButton>
                        </>
                    )}
                </Stack>

                {/* Expand button (only shown when collapsed) */}
                {!open && (
                    <div className="small-sidebar-button">
                        <IconButton color="default" size="small" onClick={handleDrawerOpen}>
                            <img src={SideBarIcon} width="43px" height="43px" alt="Expand sidebar" />
                        </IconButton>
                    </div>
                )}

                {/* ── Web-platform nav (full feature set) ───────────────────── */}
                {authData?.allowedPlatforms?.includes('web') && (
                    <div>
                        {/* "NEW" quick-create button */}
                        <div style={{ justifyContent: 'center', display: 'flex' }}>
                            {open ? (
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    id="demo-positioned-button"
                                    aria-controls={newMenuOpen ? 'demo-positioned-menu' : undefined}
                                    aria-haspopup="true"
                                    aria-expanded={newMenuOpen ? 'true' : undefined}
                                    onClick={handleOpenNewMenu}
                                    style={{
                                        width: '100%',
                                        borderRadius: '22px',
                                        border: '1px solid white',
                                        margin: '40px 16px 0 16px',
                                    }}
                                >
                                    <AddIcon fontSize="medium" sx={{ mr: '4px' }} /> NEW
                                </Button>
                            ) : (
                                // Collapsed – just show the + icon
                                <AddIcon
                                    style={{
                                        borderRadius: '15px',
                                        width: '40px',
                                        marginTop: '20px',
                                        marginBottom: '20px',
                                        cursor: 'pointer',
                                        padding: '7px',
                                        border: '1px solid white',
                                    }}
                                    fontSize="large"
                                    aria-controls={newMenuOpen ? 'demo-positioned-menu' : undefined}
                                    aria-haspopup="true"
                                    aria-expanded={newMenuOpen ? 'true' : undefined}
                                    onClick={handleOpenNewMenu}
                                    sx={{ bgcolor: 'secondary', color: 'white' }}
                                />
                            )}

                            {/* Quick-create dropdown menu */}
                            <Menu
                                sx={{ width: '80%' }}
                                id="demo-positioned-menu"
                                aria-labelledby="demo-positioned-button"
                                anchorEl={anchorEl}
                                open={newMenuOpen}
                                onClose={handleCloseNewMenu}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                className={open ? 'openSidebar-button-menu' : 'closeSidebar-button-menu'}
                            >
                                {[
                                    { label: 'Customer', icon: <PeopleIcon />, path: '/customers/new-customer' },
                                    { label: 'Appointment', icon: <ContentPasteIcon />, path: '/appointment/new-appointment' },
                                    { label: 'Invoice', icon: <ReceiptIcon />, path: '/invoice/new' },
                                    { label: 'Estimate', icon: <RequestQuoteIcon />, path: '/estimates/new' },
                                    {
                                        label: 'Work Order',
                                        icon: (
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                <path
                                                    d="M11 21.9981H3.00004L3 3.5H7.00004M15 3.5H19V14"
                                                    stroke="black"
                                                    stroke-width="1.5"
                                                    stroke-linecap="square"
                                                />
                                                <path
                                                    d="M13.5 2H8.5C7.67157 2 7 2.67157 7 3.5C7 4.32843 7.67157 5 8.5 5H13.5C14.3284 5 15 4.32843 15 3.5C15 2.67157 14.3284 2 13.5 2Z"
                                                    fill="black"
                                                    stroke="black"
                                                    stroke-width="1.5"
                                                    stroke-linecap="square"
                                                />
                                                <path d="M7 15H10.4286M7 11H15" stroke="black" stroke-width="1.5" stroke-linecap="square" />
                                                <path d="M21 17L16 22L14 20" stroke="black" stroke-width="1.5" stroke-linecap="square" />
                                            </svg>
                                        ),
                                        path: '/work-orders/new',
                                    },
                                ].map(({ label, icon, path, onClick }) => (
                                    <MenuItem
                                        key={label}
                                        onClick={() => {
                                            handleCloseNewMenu();
                                            if (path) {
                                                handleNavigation(path);
                                            } else {
                                                onClick();
                                            }
                                        }}
                                        sx={{ width: '192px' }}
                                    >
                                        {React.cloneElement(icon, { style: { marginRight: '10px' } })}
                                        {label}
                                    </MenuItem>
                                ))}
                            </Menu>
                        </div>

                        <Divider />

                        {/* ── Main nav list ──────────────────────────────────── */}
                        <List
                            sx={{
                                textAlign: 'center',
                                marginLeft: open ? '16px' : '2px',
                                marginRight: open ? '21px' : '12px',
                                paddingTop: '16px!important',
                            }}
                            onClick={() => localStorage.removeItem('searchValue')}
                        >
                            {/* ╔══════════════════════════════════════════════════╗ */}
                            {/* ║  FRONT DESK – customer-facing day-to-day ops    ║ */}
                            {/* ╚══════════════════════════════════════════════════╝ */}
                            <NavSectionLabel label="Front Desk" open={open} showDivider={false} />

                            {/* Dashboard */}
                            <NavItem
                                icon={<HomeIcon />}
                                label="Dashboard"
                                path="/"
                                activeSegment=""
                                open={open}
                                onClick={handleNavigation}
                                location={location}
                            />

                            {/* Requests  */}
                            <NavItem
                                icon={
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <path
                                            d="M13.4625 20.5375C12.4875 19.5625 12 18.3833 12 17C12 15.6167 12.4875 14.4375 13.4625 13.4625C14.4375 12.4875 15.6167 12 17 12C18.3833 12 19.5625 12.4875 20.5375 13.4625C21.5125 14.4375 22 15.6167 22 17C22 18.3833 21.5125 19.5625 20.5375 20.5375C19.5625 21.5125 18.3833 22 17 22C15.6167 22 14.4375 21.5125 13.4625 20.5375ZM18.675 19.375L19.375 18.675L17.5 16.8V14H16.5V17.2L18.675 19.375ZM5 21C4.45 21 3.97917 20.8042 3.5875 20.4125C3.19583 20.0208 3 19.55 3 19V5C3 4.45 3.19583 3.97917 3.5875 3.5875C3.97917 3.19583 4.45 3 5 3H9.175C9.35833 2.41667 9.71667 1.9375 10.25 1.5625C10.7833 1.1875 11.3667 1 12 1C12.6667 1 13.2625 1.1875 13.7875 1.5625C14.3125 1.9375 14.6667 2.41667 14.85 3H19C19.55 3 20.0208 3.19583 20.4125 3.5875C20.8042 3.97917 21 4.45 21 5V11.25C20.7 11.0333 20.3833 10.85 20.05 10.7C19.7167 10.55 19.3667 10.4167 19 10.3V5H17V8H7V5H5V19H10.3C10.4167 19.3667 10.55 19.7167 10.7 20.05C10.85 20.3833 11.0333 20.7 11.25 21H5ZM12.7125 4.7125C12.9042 4.52083 13 4.28333 13 4C13 3.71667 12.9042 3.47917 12.7125 3.2875C12.5208 3.09583 12.2833 3 12 3C11.7167 3 11.4792 3.09583 11.2875 3.2875C11.0958 3.47917 11 3.71667 11 4C11 4.28333 11.0958 4.52083 11.2875 4.7125C11.4792 4.90417 11.7167 5 12 5C12.2833 5 12.5208 4.90417 12.7125 4.7125Z"
                                            fill="white"
                                        />
                                    </svg>
                                }
                                label="Requests"
                                path="/requests/outstanding"
                                activeSegment="requests"
                                open={open}
                                onClick={handleNavigation}
                                badge={totalReqCount}
                                location={location}
                            />

                            {/* Appointments (expandable – Today / Upcoming / Past) */}
                            <NavAccordion
                                icon={<ContentPasteIcon />}
                                label="Appointments"
                                rootPath="/appointment/today"
                                activeSegment="appointment"
                                open={open}
                                onClick={handleNavigation}
                                location={location}
                            >
                                <NavSubItem
                                    label="Today"
                                    segment="today"
                                    path="/appointment/today"
                                    open={open}
                                    onClick={handleNavigation}
                                    location={location}
                                    extraStyle={{ marginTop: '-10px' }}
                                />
                                <NavSubItem
                                    label="Upcoming Appointments"
                                    segment="upcoming-appointments"
                                    path="/appointment/upcoming-appointments"
                                    open={open}
                                    onClick={handleNavigation}
                                    location={location}
                                />
                                <NavSubItem
                                    label="Past Appointments"
                                    segment="past-appointments"
                                    path="/appointment/past-appointments"
                                    open={open}
                                    onClick={handleNavigation}
                                    location={location}
                                />
                                {/* <NavSubItem
                                    label="Appointment Requests"
                                    segment="appointment-requests"
                                    path="/requests/outstanding"
                                    open={open}
                                    onClick={handleNavigation}
                                    location={location}
                                    extraStyle={{ marginBottom: '-18px' }}
                                /> */}
                            </NavAccordion>

                            {/* Calendar (no sub-items) */}
                            <NavItem
                                icon={<CalendarTodayIcon />}
                                label="Calendar"
                                path="/calendar"
                                activeSegment="calendar"
                                open={open}
                                onClick={handleNavigation}
                                location={location}
                            />

                            {/* Customers (expandable – Merge) */}
                            <NavAccordion
                                icon={<PeopleIcon />}
                                label="Customers"
                                rootPath="/customers"
                                activeSegment="customers"
                                open={open}
                                onClick={handleNavigation}
                                location={location}
                                accordionSummaryClassName={
                                    (location.pathname.split('/')[1] === 'customers' && location.pathname.split('/').length === 2 ? 'sidebarActive' : '') +
                                    (!open ? ' sidebarListItem' : ' ')
                                }
                            >
                                <NavSubItem
                                    label="Merge"
                                    segment="merge"
                                    path="/customers/merge"
                                    open={open}
                                    onClick={handleNavigation}
                                    location={location}
                                    extraStyle={{ marginTop: '-10px' }}
                                />
                            </NavAccordion>

                            {/* Messenger (with unread badge) */}
                            <NavItem
                                icon={<SmsIcon />}
                                label="Messenger"
                                path="/messenger"
                                activeSegment="messenger"
                                open={open}
                                onClick={handleNavigation}
                                badge={unreadCount}
                                location={location}
                            />

                            {/* ╔══════════════════════════════════════════════════╗ */}
                            {/* ║  BACK OFFICE – internal financial & ops mgmt    ║ */}
                            {/* ╚══════════════════════════════════════════════════╝ */}
                            <NavSectionLabel label="Back Office" open={open} />

                            {/* Invoices (expandable – All / Finalized / Sent / Draft) */}
                            <NavAccordion
                                icon={<ReceiptIcon />}
                                label="Invoices"
                                rootPath="/invoice/all"
                                activeSegment="invoice"
                                open={open}
                                onClick={handleNavigation}
                                location={location}
                            >
                                <NavSubItem
                                    label="All"
                                    segment="all"
                                    path="/invoice/all"
                                    open={open}
                                    onClick={handleNavigation}
                                    location={location}
                                    extraStyle={{ marginTop: '-10px' }}
                                />
                                <NavSubItem
                                    label="Finalized"
                                    segment="finalized"
                                    path="/invoice/finalized"
                                    open={open}
                                    onClick={handleNavigation}
                                    location={location}
                                />
                                <NavSubItem label="Sent" segment="sents" path="/invoice/sents" open={open} onClick={handleNavigation} location={location} />
                                <NavSubItem
                                    label="Draft"
                                    segment="drafts"
                                    path="/invoice/drafts"
                                    open={open}
                                    onClick={handleNavigation}
                                    location={location}
                                    extraStyle={{ marginBottom: '-18px' }}
                                />
                            </NavAccordion>

                            {/* Estimates (expandable – All / Finalized / Sent / Invoiced / Drafts) */}
                            <NavAccordion
                                icon={<RequestQuoteIcon />}
                                label="Estimates"
                                rootPath="/estimates/all"
                                activeSegment="estimates"
                                open={open}
                                onClick={handleNavigation}
                                location={location}
                            >
                                <NavSubItem
                                    label="All"
                                    segment="all"
                                    path="/estimates/all"
                                    open={open}
                                    onClick={handleNavigation}
                                    location={location}
                                    extraStyle={{ marginTop: '-10px' }}
                                />
                                <NavSubItem
                                    label="Finalized"
                                    segment="finalized"
                                    path="/estimates/finalized"
                                    open={open}
                                    onClick={handleNavigation}
                                    location={location}
                                />
                                <NavSubItem label="Sent" segment="sents" path="/estimates/sents" open={open} onClick={handleNavigation} location={location} />
                                <NavSubItem
                                    label="Invoiced"
                                    segment="invoiced"
                                    path="/estimates/invoiced"
                                    open={open}
                                    onClick={handleNavigation}
                                    location={location}
                                />
                                <NavSubItem
                                    label="Drafts"
                                    segment="drafts"
                                    path="/estimates/drafts"
                                    open={open}
                                    onClick={handleNavigation}
                                    location={location}
                                    extraStyle={{ marginBottom: '-18px' }}
                                />
                            </NavAccordion>

                            {/* Work Orders (expandable – All / Open / Authorized / Invoiced / Drafts) */}
                            <NavAccordion
                                icon={<WorkOrderIcon />}
                                label="Work Orders"
                                rootPath="/work-orders/all"
                                activeSegment="work-orders"
                                open={open}
                                onClick={handleNavigation}
                                location={location}
                            >
                                <NavSubItem
                                    label="All"
                                    segment="all"
                                    path="/work-orders/all"
                                    open={open}
                                    onClick={handleNavigation}
                                    location={location}
                                    extraStyle={{ marginTop: '-10px' }}
                                />
                                <NavSubItem label="Open" segment="open" path="/work-orders/open" open={open} onClick={handleNavigation} location={location} />
                                <NavSubItem
                                    label="Authorized"
                                    segment="authorized"
                                    path="/work-orders/authorized"
                                    open={open}
                                    onClick={handleNavigation}
                                    location={location}
                                />
                                <NavSubItem
                                    label="Invoiced"
                                    segment="invoiced"
                                    path="/work-orders/invoiced"
                                    open={open}
                                    onClick={handleNavigation}
                                    location={location}
                                />
                                <NavSubItem
                                    label="Drafts"
                                    segment="drafts"
                                    path="/work-orders/drafts"
                                    open={open}
                                    onClick={handleNavigation}
                                    location={location}
                                    extraStyle={{ marginBottom: '-18px' }}
                                />
                            </NavAccordion>

                            {/* Inventory (no sub-items) */}
                            <NavItem
                                icon={<InventoryIcon />}
                                label="Inventory"
                                path="/inventory/all"
                                activeSegment="inventory"
                                open={open}
                                onClick={handleNavigation}
                                location={location}
                            />

                            {/* Suppliers (expandable – Listing / Invoices) */}
                            <NavAccordion
                                icon={<SupplierIcon />}
                                label="Suppliers"
                                rootPath="/suppliers"
                                activeSegment="suppliers"
                                open={open}
                                onClick={handleNavigation}
                                location={location}
                            >
                                <NavSubItem
                                    label="Listing"
                                    segment="suppliers"
                                    path="/suppliers"
                                    open={open}
                                    onClick={handleNavigation}
                                    location={location}
                                    extraStyle={{ marginTop: '-10px' }}
                                />
                                <NavSubItem
                                    label="Invoices"
                                    segment="suppliers-invoice"
                                    path="/suppliers-invoice/all"
                                    open={open}
                                    onClick={handleNavigation}
                                    location={location}
                                />
                            </NavAccordion>

                            {/* Approvals (expandable – Waiting / Responded / Deferred) */}
                            <NavAccordion
                                icon={<ChecklistIcon />}
                                label="Approvals"
                                rootPath="/approvals/waiting"
                                activeSegment="approvals"
                                open={open}
                                onClick={handleNavigation}
                                location={location}
                            >
                                <NavSubItem
                                    label="Waiting"
                                    segment="waiting"
                                    path="/approvals/waiting"
                                    open={open}
                                    onClick={handleNavigation}
                                    location={location}
                                    extraStyle={{ marginTop: '-10px' }}
                                />
                                <NavSubItem
                                    label="Responded"
                                    segment="responded"
                                    path="/approvals/responded"
                                    open={open}
                                    onClick={handleNavigation}
                                    location={location}
                                />
                                <NavSubItem
                                    label="Deferred"
                                    segment="deferred"
                                    path="/approvals/deferred"
                                    open={open}
                                    onClick={handleNavigation}
                                    location={location}
                                    extraStyle={{ marginBottom: '-18px' }}
                                />
                            </NavAccordion>

                            {/* ╔══════════════════════════════════════════════════╗ */}
                            {/* ║  GROWTH – customer satisfaction & reputation    ║ */}
                            {/* ╚══════════════════════════════════════════════════╝ */}
                            <NavSectionLabel label="Growth" open={open} />

                            {/* Reviews (no sub-items) */}
                            <NavItem
                                icon={<Grade />}
                                label="Reviews"
                                path="/reviews"
                                activeSegment="reviews"
                                open={open}
                                onClick={handleNavigation}
                                location={location}
                            />
                        </List>
                    </div>
                )}

                {/* ── Mobile-only upsell CTA ─────────────────────────────────── */}
                {authData?.allowedPlatforms?.length === 1 && authData.allowedPlatforms[0] === 'mobile' && open && (
                    <div style={{ justifyContent: 'center', display: 'flex' }}>
                        <Button
                            variant="contained"
                            style={{
                                width: '80%',
                                backgroundColor: '#75DBFF',
                                borderRadius: '20px',
                                cursor: 'unset',
                                margin: '40px 16px',
                            }}
                            onClick={() => handleNavigation('/account-settings/billing-plans')}
                        >
                            SWITCH TO PREMIUM
                        </Button>
                    </div>
                )}

                {/* ── Bottom nav: Settings + Contact Support ─────────────────── */}
                {/* These are grouped under the "Admin" section label          */}
                {authData?.subscriptionStatus === 'active' && (
                    <div style={{ marginTop: 'auto' }}>
                        <List
                            sx={{
                                textAlign: 'center',
                                marginLeft: open ? '16px' : '2px',
                                marginRight: open ? '21px' : '12px',
                                paddingTop: '0!important',
                            }}
                        >
                            {/* ╔══════════════════════════════════════════════════╗ */}
                            {/* ║  ADMIN – account configuration & support        ║ */}
                            {/* ╚══════════════════════════════════════════════════╝ */}
                            <NavSectionLabel label="Admin" open={open} />
                            <NavItem
                                icon={<SettingsIcon />}
                                label="Settings"
                                path="/account-settings/personal-information"
                                activeSegment="account-settings"
                                open={open}
                                onClick={handleNavigation}
                                location={location}
                            />
                            <NavItem
                                icon={<CallIcon />}
                                label="Contact Support"
                                path="/contact-support"
                                activeSegment="contact-support"
                                open={open}
                                onClick={handleNavigation}
                                location={location}
                            />
                        </List>
                    </div>
                )}
            </Drawer>

            {/* ── Modals ─────────────────────────────────────────────────────── */}

            {/* Toast shown after toggling the pause-appointments switch */}
            <SuccessMessageModal message={successMessage} open={openSuccessModal} setOpenSuccessModal={setOpenSuccessModal} />

            {/* Unsaved-changes confirmation dialog */}
            <CommonDialog
                open={discardModalOpen}
                onClose={handleDiscardCancel}
                onSave={handleDiscardConfirm}
                dialogTitle={
                    pathSegments.includes('customer-notifications')
                        ? '⚠️ Discard message changes?'
                        : discardModalTitle
                          ? `⚠️ Discard ${discardModalTitle.replace(/\b\w/g, (c) => c.toUpperCase())}${pathSegments.includes('record-payment') ? '' : '?'}`
                          : '⚠️ Discard?'
                }
                dialogSubtitle={
                    pathSegments.includes('customer-notifications')
                        ? 'You have unsaved changes to your messages. Discard changes or continue editing?'
                        : discardModalDescription
                }
                saveButtonText="Discard"
            />

            <AddQuickReviewModal
                open={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                prefetchedQuickReviewUsers={quickReviewUsers}
                prefetchedCustomersPage1={prefetchedCustomersPage1}
                prefetchedCustomersPage1Meta={prefetchedCustomersPage1Meta}
            />
        </>
    );
}
